import { useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { Server } from 'lucide-react';
import { Skeleton, Button } from '@vnetwork/ui/primitives';
import { PageHeader, Breadcrumbs, BreadcrumbItem, Container } from '@vnetwork/ui/layout';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useInstanceDetail,
  useOpennebula,
  instanceHasSettled,
  getPollingTimeout,
} from '@/api/opennebula';
import { instanceStateKey, instanceTone, instanceLabel } from '../shared';
import { OverviewTab } from './OverviewTab';
import { MonitoringTab } from './MonitoringTab';
import { NetworkTab } from './NetworkTab';
import { VolumeTab } from './VolumeTab';
import { SnapshotTab } from './SnapshotTab';
import { BackupTab } from './BackupTab';
import { NetworkTrafficTab } from './NetworkTrafficTab';
import { InstanceActionBar } from './InstanceActionBar';
import { InstanceResize } from './InstanceResize';

// Instance sub-sections live in the app's ContextualSidebar (INSTANCE_NAV in
// ../nav.ts, registered by ComputeConsole) — keep its slugs in sync with the
// routes below.

export function InstanceDetail() {
  const navigate = useNavigate();
  const { serviceId, instanceId } = useParams<{ serviceId: string; instanceId: string }>();
  const { isWaitingUpdate, setWaiting } = useOpennebula();
  const { data: instance, isLoading, isError, refetch } = useInstanceDetail(serviceId, instanceId, isWaitingUpdate);

  // An action raises the waiting flag to poll this page faster while the
  // instance moves. Nothing lowered it again, so once any action had run the
  // page refetched every 3s for as long as it stayed open — and each refetch
  // reads the VM from OpenNebula.
  //
  // It comes down when the instance has moved somewhere and stopped, and in any
  // case after the action's own timeout, which the polling profiles have always
  // carried. The timer restarts on each new reading, so a transition that keeps
  // reporting progress keeps its fast polling.
  const statusWhenRaised = useRef<string | undefined>(undefined);
  const hasMoved = useRef(false);
  useEffect(() => {
    if (!isWaitingUpdate) {
      statusWhenRaised.current = undefined;
      hasMoved.current = false;
      return;
    }
    if (statusWhenRaised.current === undefined) statusWhenRaised.current = instance?.status ?? '';

    // Having moved is remembered, not re-tested: a reboot leaves and returns to
    // the same status, so a check against the current reading is false again by
    // the time the instance settles, and the flag would stay up to its timeout.
    if ((instance?.status ?? '') !== statusWhenRaised.current) hasMoved.current = true;

    if (hasMoved.current && instanceHasSettled(instance)) {
      setWaiting(false);
      return;
    }

    const giveUp = setTimeout(() => setWaiting(false), getPollingTimeout(isWaitingUpdate));
    return () => clearTimeout(giveUp);
  }, [isWaitingUpdate, instance, setWaiting]);

  const instancesPath = `/services/compute/${serviceId}/instances`;
  const stateKey = instanceStateKey(instance?.state, instance?.status);

  if (isLoading) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 28, width: 260, marginBottom: 'var(--vn-space-4)' }} />
        <Skeleton style={{ height: 280 }} />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container size="xl">
        <EmptyState
          icon={<Server size={28} />}
          title="Couldn't load instance"
          description="Something went wrong fetching this instance. It may be temporarily unavailable."
          actions={
            <Button variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      </Container>
    );
  }

  if (!instance || instance.status === 'TERMINATED') {
    return (
      <Container size="xl">
        <EmptyState
          icon={<Server size={28} />}
          title={instance?.status === 'TERMINATED' ? 'Instance terminated' : 'Instance not found'}
          description={
            instance?.status === 'TERMINATED'
              ? `“${instance?.name || instanceId}” has been terminated. Create a new one to continue.`
              : "It may have been deleted, or you don't have access to it."
          }
          actions={
            <Button variant="secondary" onClick={() => navigate(instancesPath)}>
              Back to instances
            </Button>
          }
        />
      </Container>
    );
  }

  return (
    <Container size="xl">
      {/* Header */}
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem
              href={instancesPath}
              onClick={(e) => {
                e.preventDefault();
                navigate(instancesPath);
              }}
            >
              Instances
            </BreadcrumbItem>
            <BreadcrumbItem current>{instance.name || instanceId}</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-3)', minWidth: 0 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {instance.name || instanceId}
            </span>
            <StatusPill tone={instanceTone(stateKey)}>{instanceLabel(stateKey)}</StatusPill>
          </span>
        }
        actions={
          <InstanceActionBar
            serviceId={serviceId!}
            instance={instance}
            onAfterTerminate={() => navigate(instancesPath)}
          />
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {/* Status alerts */}
        {instance.status === 'PENDING' && (
          <Alert tone="info">
            Instance <code>{instance.name}</code> is being prepared. Allow a few minutes for setup to finish.
          </Alert>
        )}
        {(instance.state === 'BACKUP' || instance.state === 'BACKUP_POWEROFF') && (
          <Alert tone="warning">
            A backup is in progress for <code>{instance.name}</code>. Some actions are temporarily unavailable.
          </Alert>
        )}

        <Routes>
        <Route index element={<OverviewTab serviceId={serviceId!} instance={instance} />} />
        <Route path="monitoring" element={<MonitoringTab serviceId={serviceId!} instance={instance} />} />
        <Route path="network" element={<NetworkTab serviceId={serviceId!} instance={instance} />} />
        <Route path="traffic" element={<NetworkTrafficTab serviceId={serviceId!} instance={instance} />} />
        <Route path="volumes" element={<VolumeTab serviceId={serviceId!} instance={instance} />} />
        <Route path="snapshots" element={<SnapshotTab serviceId={serviceId!} instance={instance} />} />
        <Route path="backups" element={<BackupTab serviceId={serviceId!} instance={instance} />} />
        <Route path="resize" element={<><OverviewTab serviceId={serviceId!} instance={instance} /><InstanceResize serviceId={serviceId!} instance={instance} /></>} />
        <Route path="*" element={<OverviewTab serviceId={serviceId!} instance={instance} />} />
        </Routes>
      </div>
    </Container>
  );
}
